import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/Button";
import { cn } from "@/lib/cn";

/*
 * Sticky save bar for sectioned editors.
 *
 * Always rendered in the DOM but visually hidden (opacity 0 + translated
 * down) until `dirty` is true. Slides up + fades in when there are unsaved
 * changes, slides back out when in sync. Inner pill toggles
 * pointer-events so clicks only register when visible.
 */

interface Props {
  dirty: boolean;
  onSave: () => Promise<void> | void;
  onDiscard: () => void;
  saveLabel?: string;
  /** When false, the save button is disabled (bar stays visible). Defaults true. */
  canSave?: boolean;
  /** Optional caption next to the buttons (defaults to "Unsaved changes"). */
  caption?: ReactNode;
}

export function SectionSaveBar({
  dirty,
  onSave,
  onDiscard,
  saveLabel = "Save changes",
  canSave = true,
  caption,
}: Props) {
  const [submitting, setSubmitting] = useState(false);

  return (
    <div
      aria-hidden={!dirty}
      className={cn(
        "pointer-events-none sticky bottom-2 z-10 flex justify-center pt-4",
        "transition-all duration-[var(--duration-default)] ease-[var(--ease-out)]",
        dirty ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
      )}
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-[var(--radius-lg)] border border-border-subtle bg-surface px-3 py-2 shadow-[var(--shadow-3)]">
        <span className="px-1 text-sm text-fg-muted">
          {caption ?? "Unsaved changes"}
        </span>
        <Button intent="ghost" size="sm" onClick={onDiscard} disabled={submitting}>
          Discard
        </Button>
        <Button
          intent="primary"
          size="sm"
          disabled={submitting || !canSave}
          onClick={async () => {
            setSubmitting(true);
            try {
              await onSave();
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting && <Loader2 className="animate-spin" />}
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}
